import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Bell, TrendingUp, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailPreferences = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    weekly_summary: true,
    promotional: true,
    streak_reminders: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await axios.get(`${API}/email/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(res.data);
    } catch (err) {
      console.error('Failed to fetch email preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${API}/email/preferences`, { [key]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Preferences saved');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setPreferences(preferences);
      setMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setMessage('');
    try {
      await axios.post(`${API}/email/send-weekly-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Weekly summary sent to your email!');
    } catch (err) {
      const d = err.response?.data?.detail;
      setMessage(typeof d === 'string' ? d : 'Failed to send test email');
    } finally {
      setSendingTest(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const sendTestStreakAlert = async () => {
    setSendingTest(true);
    setMessage('');
    try {
      await axios.post(`${API}/email/send-streak-alert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Streak alert sent to your email!');
    } catch (err) {
      const d2 = err.response?.data?.detail;
      setMessage(typeof d2 === 'string' ? d2 : 'Failed to send streak alert');
    } finally {
      setSendingTest(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4"></div>
        <div className="space-y-4">
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>
        </div>
      </div>
    );
  }

  const emailOptions = [
    {
      key: 'weekly_summary',
      icon: TrendingUp,
      title: 'Weekly Summary',
      description: 'Get your weekly calorie, exercise, and streak stats every Monday',
      color: 'text-[#FF6600]',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
    },
    {
      key: 'streak_reminders',
      icon: Bell,
      title: 'Streak Reminders',
      description: 'Reminders to keep your logging streak alive',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      key: 'promotional',
      icon: Mail,
      title: 'Tips & Updates',
      description: 'Nutrition tips, new features, and health insights',
      color: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden" data-testid="email-preferences">
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
            <Mail className="text-[#FF6600]" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Email Notifications</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage what emails you receive</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-3">
        {emailOptions.map(({ key, icon: Icon, title, description, color, bgColor }) => (
          <div
            key={key}
            className="flex items-center justify-between p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:border-gray-700 dark:hover:border-gray-600 transition-colors"
            data-testid={`email-pref-${key}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-9 h-9 ${bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={color} size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm text-gray-900 dark:text-white">{title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{description}</div>
              </div>
            </div>
            <button
              data-testid={`email-toggle-${key}`}
              onClick={() => updatePreference(key, !preferences[key])}
              disabled={saving}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-3 ${
                preferences[key] ? 'bg-[#FF6600]' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white dark:bg-gray-800 rounded-full shadow transition-transform ${
                preferences[key] ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
        ))}
      </div>

      <div className="px-6 pb-6 space-y-3">
        <button
          data-testid="send-test-weekly-email"
          onClick={sendTestEmail}
          disabled={sendingTest || !preferences.weekly_summary}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 bg-[#FF6600] hover:bg-[#E55C00] text-white"
        >
          <Send size={16} />
          {sendingTest ? 'Sending...' : 'Send Test Weekly Summary'}
        </button>
        <button
          data-testid="send-test-streak-alert"
          onClick={sendTestStreakAlert}
          disabled={sendingTest || !preferences.streak_reminders}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 border-2 border-[#FF6600] text-[#FF6600] hover:bg-orange-50"
        >
          <Bell size={16} />
          {sendingTest ? 'Sending...' : 'Send Test Streak Alert'}
        </button>
        {!preferences.weekly_summary && !preferences.streak_reminders && (
          <p className="text-xs text-gray-400 text-center">Enable email types above to send test emails</p>
        )}
        {message && (
          <p className={`text-xs text-center font-medium ${message.includes('Failed') ? 'text-red-500' : 'text-green-500'}`} data-testid="email-pref-message">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailPreferences;
